import React from 'react';

import { Thead, Tr } from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';

import { Table, Tbody, Th, Td } from '../../../pages/_layout/AdminLayout.styles';

import { Container, EmptyRow, LoadingRow } from './styles';

export interface DataTableColumn<T> {
  /** Identificador único da coluna. */
  key: string;
  /** Cabeçalho visível. */
  header: string;
  /** Conteúdo da célula. */
  render: (row: T) => React.ReactNode;
  /** Alinhamento (padrão: centro). */
  align?: 'start' | 'center' | 'end';
  /** Largura opcional (ex.: "50%"). */
  width?: string;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  rows: T[];
  /** Chave estável de cada linha. */
  rowKey: (row: T) => string;
  /** Texto do estado vazio (quando `rows` está vazio). */
  emptyMessage?: string;
  /** Exibe esqueleto de carregamento. */
  loading?: boolean;
  /** Torna a linha inteira clicável. */
  onRowClick?: (row: T) => void;
}

/**
 * Tabela do admin: uma implementação só, com colunas declarativas, estados de
 * carregamento/vazio embutidos e linha opcionalmente clicável. Substitui as
 * tabelas duplicadas de cursos, usuários, banners, seções, cupons e vendas.
 */
function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage = 'Nenhum registro encontrado.',
  loading = false,
  onRowClick,
}: DataTableProps<T>) {
  const alignStyle = (align?: 'start' | 'center' | 'end') => ({
    textAlign: align ?? ('center' as const),
  });

  return (
    <Container>
      <Table breakpoint={1000}>
        <Thead>
          <Tr>
            {columns.map((column) => (
              <Th key={column.key} style={{ ...alignStyle(column.align), width: column.width }}>
                {column.header}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {loading && (
            <Tr>
              <Td colSpan={columns.length}>
                <LoadingRow>Carregando…</LoadingRow>
              </Td>
            </Tr>
          )}

          {!loading && rows.length === 0 && (
            <Tr>
              <Td colSpan={columns.length}>
                <EmptyRow>{emptyMessage}</EmptyRow>
              </Td>
            </Tr>
          )}

          {!loading &&
            rows.map((row) => (
              <Tr
                key={rowKey(row)}
                withInteraction={!!onRowClick}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((column) => (
                  <Td key={column.key} style={alignStyle(column.align)}>
                    {column.render(row)}
                  </Td>
                ))}
              </Tr>
            ))}
        </Tbody>
      </Table>
    </Container>
  );
}

export default DataTable;
